/**
 * ResizeObserver management for element size monitoring.
 * Provides efficient element resize tracking with .NET callbacks.
 */

'use strict';

import type { DotNetObjectReference, SizeInfo } from '../types.js';
import { safeInvokeSync } from '../utils/dotnet.js';

/**
 * Internal state for ResizeObserver functionality.
 */
interface ResizeObserverState {
    callbackMap: WeakMap<Element, (width: number, height: number) => void>;
    observer: ResizeObserver | null;
}

const state: ResizeObserverState = {
    callbackMap: new WeakMap(),
    observer: null
};

/**
 * Get or create the shared ResizeObserver instance.
 * 
 * @returns ResizeObserver instance
 */
function getOrCreateObserver(): ResizeObserver {
    if (!state.observer) {
        state.observer = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const callback = state.callbackMap.get(entry.target);
                if (!callback) {
                    continue;
                }

                const rect = entry.contentRect || {
                    width: (entry.target as HTMLElement).clientWidth,
                    height: (entry.target as HTMLElement).clientHeight
                };

                try {
                    callback(rect.width, rect.height);
                } catch {
                    // Ignore callback errors
                }
            }
        });
    }

    return state.observer;
}

/**
 * Get element size from bounding rect or client dimensions.
 * 
 * @param element - Element to measure
 * @returns Size information
 */
function getElementSize(element: Element): SizeInfo {
    try {
        const rect = element.getBoundingClientRect?.();
        return {
            width: rect?.width ?? (element as HTMLElement).clientWidth,
            height: rect?.height ?? (element as HTMLElement).clientHeight
        };
    } catch {
        return { width: 0, height: 0 };
    }
}

/**
 * Observe an element size using ResizeObserver and notify .NET.
 * If the element is already being observed, the callback is replaced.
 * 
 * @param element - Element to observe
 * @param dotnetHelper - .NET object reference for callbacks
 * @returns True if successfully started observing
 */
export function observe(
    element: Element | null,
    dotnetHelper: DotNetObjectReference
): boolean {
    try {
        if (!element) {
            return false;
        }

        const observer = getOrCreateObserver();

        // If already observing this element, unobserve first to replace callback
        if (state.callbackMap.has(element)) {
            try {
                observer.unobserve(element);
            } catch {
                // Ignore errors during unobserve
            }
        }

        // Create callback that notifies .NET
        const callback = (width: number, height: number) => {
            const sizeInfo: SizeInfo = { width, height };
            safeInvokeSync(dotnetHelper, 'OnSizeChanged', sizeInfo);
        };

        state.callbackMap.set(element, callback);
        observer.observe(element);

        // Fire initial size to ensure .NET receives first measurement
        const initialSize = getElementSize(element);
        callback(initialSize.width, initialSize.height);

        return true;
    } catch {
        return false;
    }
}

/**
 * Stop observing a specific element.
 * 
 * @param element - Element to stop observing
 */
export function unobserve(element: Element | null): void {
    try {
        if (!element || !state.observer) {
            return;
        }

        state.callbackMap.delete(element);
        state.observer.unobserve(element);

        // Note: We keep the observer alive for other elements
        // WeakMaps don't have a size property, so we can't check if empty
    } catch {
        // Ignore errors during unobserve
    }
}
